<script>
import { mapGetters, mapActions } from "vuex";
import { useI18n } from "vue-i18n";
import store from "../store";

export default {
  name: "AppHeader",
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      showLogoutModal: false,
      drawer: false,
    };
  },
  computed: {
    ...mapGetters(["isLoggedIn"]),
    role() {
      return store.getters.user?.role || null;
    },
    isMobile() {
      // Visa meny om bredden är mindre än smmd (800px)
      return (
        this.$vuetify.display.width < this.$vuetify.display.thresholds.smmd
      );
    },

    currentPageLabel() {
      switch (this.$route.path) {
        case "/":
          return this.t("header.homePage");
        case "/admin":
          return "Admin";
        case "/employee":
          return this.t("header.meetings");
        case "/companies":
          return this.t("header.exhibitors");
        case "/profile":
          return this.t("header.profile");
        case "/login":
          return this.isLoggedIn
            ? this.t("login.logout")
            : this.t("login.login");
        default:
          return "";
      }
    },
  },
  methods: {
    ...mapActions(["logout"]),
    goToLogin() {
      if (!this.isLoggedIn) {
        this.$router.push("/login");
      } else {
        this.showLogoutModal = true;
      }
    },
    async confirmLogout() {
      await this.logout();
      this.showLogoutModal = false;
      this.$router.push("/login");
    },
    cancelLogout() {
      this.showLogoutModal = false;
    },
    changeLanguage(lang) {
      this.$i18n.locale = lang;
      localStorage.setItem("lang", lang);
    },
  },
  mounted() {
    const savedLang = localStorage.getItem("lang");
    if (savedLang) {
      this.$i18n.locale = savedLang;
    }
  },
};
</script>

<template>
  <v-app-bar app fixed dense color="secondary">
    <!-- Menyknapp: endast mobil -->
    <v-app-bar-nav-icon
      v-if="isMobile"
      @click="drawer = !drawer"
    ></v-app-bar-nav-icon>

    <!-- Startsida alltid till vänster på desktop -->
    <v-toolbar-title v-if="!isMobile">
      <v-btn to="/" text :class="{ 'active-btn': $route.path === '/' }">
        {{ t("header.homePage") }}
      </v-btn>
    </v-toolbar-title>

    <v-toolbar-title v-if="isMobile">
      {{ currentPageLabel.toUpperCase() }}
    </v-toolbar-title>

    <!-- Knappgruppen till höger på desktop -->
    <div v-if="!isMobile" class="align-center">
      <v-btn
        v-if="role === 'admin'"
        to="/admin"
        text
        class="mr-4"
        :class="{ 'active-btn': $route.path === '/admin' }"
      >
        Admin
      </v-btn>

      <v-btn
        v-if="role === 'employee'"
        to="/employee"
        text
        class="mr-4"
        :class="{ 'active-btn': $route.path === '/employee' }"
      >
        {{ t("header.meetings") }}
      </v-btn>

      <v-btn
        to="/companies"
        text
        class="mr-4"
        :class="{ 'active-btn': $route.path === '/companies' }"
      >
        {{ t("header.exhibitors") }}
      </v-btn>

      <v-btn
        v-if="role !== 'employee'"
        to="/profile"
        text
        class="mr-4"
        :class="{ 'active-btn': $route.path === '/profile' }"
      >
        {{ t("header.profile") }}
      </v-btn>

      <!-- Språkväljare -->
      <v-menu offset-y>
        <template #activator="{ props }">
          <v-btn text v-bind="props" class="mr-4">
            {{ t("language." + $i18n.locale) }}
            <v-icon right>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="changeLanguage('sv')">
            <v-list-item-title>Svenska</v-list-item-title>
          </v-list-item>
          <v-list-item @click="changeLanguage('en')">
            <v-list-item-title>English</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-btn
        @click="goToLogin"
        text
        :class="{ 'active-btn': $route.path === '/login' }"
      >
        {{ isLoggedIn ? t("login.logout") : t("login.login") }}
      </v-btn>
    </div>
  </v-app-bar>

  <!-- Navigation Drawer för mobil -->
  <v-navigation-drawer
    v-model="drawer"
    app
    fixed
    temporary
    left
    v-if="isMobile"
  >
    <v-list>
      <v-list-item to="/">
        <v-list-item-title>{{ t("header.homePage") }}</v-list-item-title>
      </v-list-item>

      <v-list-item to="/admin" v-if="role === 'admin'">
        <v-list-item-title>Admin</v-list-item-title>
      </v-list-item>

      <v-list-item to="/employee" v-if="role === 'employee'">
        <v-list-item-title>{{ t("header.meetings") }}</v-list-item-title>
      </v-list-item>

      <v-list-item to="/companies">
        <v-list-item-title>{{ t("header.exhibitors") }}</v-list-item-title>
      </v-list-item>

      <v-list-item to="/profile" v-if="role !== 'employee'">
        <v-list-item-title>{{ t("header.profile") }}</v-list-item-title>
      </v-list-item>

      <v-list-item @click="changeLanguage('sv')">
        <v-list-item-title>Svenska</v-list-item-title>
      </v-list-item>

      <v-list-item @click="changeLanguage('en')">
        <v-list-item-title>English</v-list-item-title>
      </v-list-item>

      <v-list-item @click="goToLogin">
        <v-list-item-title>
          {{ isLoggedIn ? t("login.logout") : t("login.login") }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>

  <!-- Logout Modal -->
  <v-dialog v-model="showLogoutModal" max-width="400">
    <v-card>
      <v-card-title class="headline">{{ t("login.logout") }}</v-card-title>
      <v-card-text>{{ t("login.logoutQuestion") }}</v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" text @click="confirmLogout">
          {{ t("login.logout") }}
        </v-btn>
        <v-btn color="error" text @click="cancelLogout">
          {{ t("common.cancel") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.v-btn {
  transition: all 0.3s ease;
}

.v-btn:hover {
  background-color: white !important;
  color: #763493 !important;
}

.active-btn {
  background-color: #763493 !important;
  color: #fbe4ff !important;
}
</style>
